import React, { useState, useEffect } from "react";

import UnsubscribeModal from "../components/UnsubscribeModal";
import { sendPostRequest, formatPhoneNumber } from "../utils";
import "./Super.css";

const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const postalPattern = /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/;
const AREA_CODE = process.env.REACT_APP_DEFAULT_AREA_CODE || "+1";

const SUPER_PRODUCT = {
  lookup_key: "24_pcs_bagels_monthly",
  name: "24 bagels par mois",
  price_label: "69.95$ CAD / mois",
  description:
    "Une seule livraison mensuelle. Emballés à l'azote pour rester frais.",
};

const Super = () => {
  const [inputs, setInputs] = useState({
    city: "",
    name: "",
    phone_number: "",
    postal_code: "",
    quantity: SUPER_PRODUCT.lookup_key,
    email: "",
    address: "",
    number: "",
    apartment: "",
    accept_sms_notification: false,
    accept_email_notification: false,
  });
  const [error, setError] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [showUnsubscribeModal, setShowUnsubscribeModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const {
    city,
    name,
    phone_number,
    postal_code,
    quantity,
    email,
    address,
    apartment,
    accept_sms_notification,
    accept_email_notification,
  } = inputs;

  const isValidPostalCode = postal_code
    ? postalPattern.test(postal_code)
    : true;

  const isDisabled =
    !city ||
    !name ||
    !phone_number ||
    !postal_code ||
    !quantity ||
    !emailPattern.test(email) ||
    isSubmitting ||
    !address ||
    !accept_sms_notification ||
    !isValidPostalCode ||
    Boolean(phoneNumberError);

  const handleChange = (event) => {
    const targetName = event.target.name;
    const value =
      targetName === "accept_sms_notification" ||
      targetName === "accept_email_notification"
        ? event.target.checked
        : event.target.value;

    if (targetName === "phone_number") {
      if (value && !/^[0-9]+$/.test(value)) return;
      if (!/^\d{10}$/.test(Number(value)) && !phoneNumberError) {
        setPhoneNumberError("Numéro de téléphone invalide");
      } else if (/^\d{10}$/.test(Number(value)) && phoneNumberError) {
        setPhoneNumberError("");
      }
    }

    setInputs((values) => ({ ...values, [targetName]: value }));
  };

  const doCheckOut = async (userId) => {
    try {
      const res = await sendPostRequest("create-checkout-session", {
        lookup_key: String(quantity),
        user_info: {
          ...inputs,
          phone_number: formatPhoneNumber(inputs.phone_number),
        },
        userId,
      });

      const body = await res.json();
      if (body?.url) {
        window.location.href = body.url;
        return;
      }

      if (body?.errMessage) setError(body.errMessage);
    } catch (err) {
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitForm = (event) => {
    event.preventDefault();
    if (isDisabled) return;
    setIsSubmitting(true);
    sendPostRequest("subscribe", {
      ...inputs,
      phone_number: formatPhoneNumber(inputs.phone_number),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data?.userId) doCheckOut(data.userId);
        if (data?.errMessage) {
          setError(data.errMessage);
          setIsSubmitting(false);
        }
      });
  };

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);

      const hasSeenPrompt = localStorage.getItem("pwa-prompt-dismissed");
      if (!hasSeenPrompt) {
        setShowInstallBanner(true);
      }
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      setShowInstallBanner(false);
      return;
    }
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShowInstallBanner(false);
  };

  const handleDismissInstall = () => {
    setShowInstallBanner(false);
    localStorage.setItem("pwa-prompt-dismissed", "true");
  };

  return (
    <div className="super-page">
      {showUnsubscribeModal ? (
        <UnsubscribeModal
          open={showUnsubscribeModal}
          handleClose={() => setShowUnsubscribeModal(false)}
        />
      ) : null}

      {isSubmitting && <div id="cover-spin"></div>}

      {/* ── INSTALL BANNER ── */}
      {showInstallBanner && (
        <div className="super-install-banner">
          <div className="super-banner-icon">
            <svg width="22" height="26" viewBox="0 0 22 26" fill="none">
              <path
                d="M11 0C6.03 0 2 4.03 2 9C2 16.5 11 26 11 26C11 26 20 16.5 20 9C20 4.03 15.97 0 11 0Z"
                fill="white"
              />
              <circle cx="11" cy="9" r="4" fill="#C84B2F" />
              <circle cx="11" cy="9" r="1.8" fill="white" />
            </svg>
          </div>
          <div className="super-banner-text">
            <strong>Installer l'application</strong>
            <span>Ajoutez Bagels Round Top à votre écran d'accueil</span>
          </div>
          <div className="super-banner-actions">
            <button className="super-btn-later" onClick={handleDismissInstall}>
              Plus tard
            </button>
            <button className="super-btn-install" onClick={handleInstallClick}>
              Installer
            </button>
          </div>
        </div>
      )}

      {/* ── NAV ── */}
      <nav className="super-nav">
        <a href="/" className="super-nav-logo">
          <svg width="30" height="36" viewBox="0 0 30 36" fill="none">
            <path
              d="M15 0C8.37 0 3 5.37 3 12C3 21 15 36 15 36C15 36 27 21 27 12C27 5.37 21.63 0 15 0Z"
              fill="#C84B2F"
            />
            <circle cx="15" cy="12" r="7" fill="#EDD9A0" opacity="0.9" />
            <circle cx="15" cy="12" r="4" fill="#D4A96A" />
            <circle cx="15" cy="12" r="2" fill="#EDD9A0" />
          </svg>
        </a>
        <ul className="super-nav-links">
          <li>
            <a href="#super-plans">Abonnement</a>
          </li>
          <li>
            <a href="/contact-us">Joignez-nous</a>
          </li>
          <li>
            <a href="/about-us">À propos de nous</a>
          </li>
          <li>
            <a href="/arbres">Arbres</a>
          </li>
          <li>
            <a href="/residents">Offres pour les résidents</a>
          </li>
        </ul>
        <button
          className={`super-nav-toggle ${mobileMenuOpen ? "is-open" : ""}`}
          onClick={() => setMobileMenuOpen((v) => !v)}
          aria-label="Menu"
          aria-expanded={mobileMenuOpen}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
        {mobileMenuOpen && (
          <ul className="super-mobile-menu">
            <li>
              <a href="#super-plans" onClick={() => setMobileMenuOpen(false)}>
                Abonnement
              </a>
            </li>
            <li>
              <a href="/contact-us" onClick={() => setMobileMenuOpen(false)}>
                Joignez-nous
              </a>
            </li>
            <li>
              <a href="/about-us" onClick={() => setMobileMenuOpen(false)}>
                À propos de nous
              </a>
            </li>
            <li>
              <a href="/arbres" onClick={() => setMobileMenuOpen(false)}>
                Arbres
              </a>
            </li>
            <li>
              <a href="/residents" onClick={() => setMobileMenuOpen(false)}>
                Offres pour les résidents
              </a>
            </li>
          </ul>
        )}
      </nav>

      {/* ── HERO ── */}
      <section className="super-hero">
        <svg
          className="super-hero-mountains"
          viewBox="0 0 1440 340"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 340L160 200L320 260L520 130L720 220L920 110L1120 200L1300 150L1440 170V340H0Z"
            fill="#4A7A30"
            opacity="0.25"
          />
          <path
            d="M0 340L200 220L380 270L580 170L760 240L960 140L1140 220L1320 175L1440 200V340H0Z"
            fill="#2A5518"
            opacity="0.45"
          />
          <path d="M560 170L580 130L600 170Z" fill="white" opacity="0.7" />
          <path d="M940 140L960 100L980 140Z" fill="white" opacity="0.65" />
          <path d="M180 220L200 175L220 220Z" fill="white" opacity="0.5" />
          <path
            d="M0 340L220 265L420 295L660 215L860 275L1060 200L1240 258L1440 230V340H0Z"
            fill="#1E3D10"
            opacity="0.75"
          />
          <g fill="#1E3D10">
            <polygon points="40,278 58,248 76,278" />
            <polygon points="34,290 58,242 82,290" />
            <polygon points="72,280 92,252 112,280" />
            <polygon points="66,292 92,246 118,292" />
            <polygon points="108,283 128,255 148,283" />
          </g>
          <g fill="#1E3D10">
            <polygon points="1290,268 1308,240 1326,268" />
            <polygon points="1284,280 1308,234 1332,280" />
            <polygon points="1320,272 1340,244 1360,272" />
            <polygon points="1350,270 1370,242 1390,270" />
            <polygon points="1382,278 1402,252 1422,278" />
          </g>
          <g transform="translate(1165, 208)" fill="#1E3D10" opacity="0.55">
            <circle cx="10" cy="0" r="6" />
            <rect x="6" y="8" width="8" height="18" rx="2" />
            <rect x="0" y="13" width="8" height="2.5" rx="1" />
            <rect x="10" y="13" width="8" height="2.5" rx="1" />
            <rect x="5" y="26" width="4.5" height="16" rx="1" />
            <rect x="10" y="26" width="4.5" height="16" rx="1" />
            <rect x="20" y="6" width="2" height="38" rx="1" />
          </g>
          <g transform="translate(250, 250)" fill="#1E3D10" opacity="0.45">
            <circle cx="10" cy="0" r="5.5" />
            <rect
              x="7"
              y="7"
              width="7"
              height="15"
              rx="2"
              transform="rotate(-5 10 14)"
            />
            <rect
              x="-2"
              y="11"
              width="12"
              height="2.5"
              rx="1"
              transform="rotate(25 5 12)"
            />
            <rect
              x="10"
              y="11"
              width="12"
              height="2.5"
              rx="1"
              transform="rotate(-30 16 12)"
            />
            <rect
              x="6"
              y="22"
              width="5"
              height="14"
              rx="1"
              transform="rotate(12 8 29)"
            />
            <rect
              x="10"
              y="22"
              width="5"
              height="14"
              rx="1"
              transform="rotate(-8 12 29)"
            />
          </g>
          <g
            transform="translate(650, 230)"
            fill="none"
            stroke="#1E3D10"
            strokeWidth="2"
            opacity="0.4"
          >
            <circle cx="10" cy="9" r="8" />
            <circle cx="36" cy="9" r="8" />
            <line x1="23" y1="-10" x2="23" y2="3" />
            <line x1="23" y1="3" x2="10" y2="9" />
            <line x1="23" y1="3" x2="36" y2="9" />
            <line x1="23" y1="-10" x2="16" y2="-2" />
            <line x1="23" y1="-10" x2="30" y2="-2" />
            <circle cx="23" cy="-14" r="5" fill="#1E3D10" stroke="none" />
          </g>
          <g transform="translate(820, 252)" fill="#1E3D10" opacity="0.4">
            <circle cx="10" cy="0" r="5" />
            <rect x="7" y="7" width="7" height="15" rx="2" />
            <rect
              x="-2"
              y="11"
              width="11"
              height="2.5"
              rx="1"
              transform="rotate(-20 4 12)"
            />
            <rect
              x="10"
              y="11"
              width="11"
              height="2.5"
              rx="1"
              transform="rotate(20 15 12)"
            />
            <rect
              x="6"
              y="22"
              width="4.5"
              height="16"
              rx="1"
              transform="rotate(8 8 30)"
            />
            <rect
              x="10"
              y="22"
              width="4.5"
              height="16"
              rx="1"
              transform="rotate(-5 12 30)"
            />
            <line
              x1="22"
              y1="5"
              x2="26"
              y2="38"
              stroke="#1E3D10"
              strokeWidth="2"
            />
          </g>
        </svg>

        <div className="super-hero-content">
          <div className="super-hero-badge">
            🔥 Cuits au feu de bois &nbsp;·&nbsp; Sutton, Québec
          </div>

          <div className="super-hero-pin">
            <svg
              viewBox="0 0 88 106"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M44 0C24.67 0 9 15.67 9 35C9 62 44 106 44 106C44 106 79 62 79 35C79 15.67 63.33 0 44 0Z"
                fill="#C84B2F"
              />
              <circle
                cx="44"
                cy="35"
                r="22"
                fill="#F7EDD5"
                stroke="#9E3820"
                strokeWidth="1.5"
              />
              <circle cx="44" cy="35" r="15.5" fill="#D4A96A" />
              <circle cx="44" cy="35" r="9" fill="#EDD9A0" />
              <circle cx="44" cy="35" r="5" fill="#F7EDD5" />
              <circle cx="34" cy="26" r="1.6" fill="#1E3D10" />
              <circle cx="54" cy="26" r="1.6" fill="#1E3D10" />
              <circle cx="29" cy="37" r="1.6" fill="#1E3D10" />
              <circle cx="59" cy="37" r="1.6" fill="#1E3D10" />
              <circle cx="36" cy="47" r="1.6" fill="#1E3D10" />
              <circle cx="52" cy="47" r="1.6" fill="#1E3D10" />
              <circle cx="44" cy="24" r="1.4" fill="#1E3D10" />
            </svg>
          </div>

          <h1 className="super-hero-title">
            ROUND TOP
            <br />
            <span>SUPER BAGEL</span>
          </h1>

          <p className="super-hero-tagline">Dévorez la performance</p>

          <div className="super-hero-protein">
            <div className="super-hero-protein-inner">
              <span>20g de protéines par bagel</span>
            </div>
          </div>

          <p className="super-hero-sub">
            Abonnez-vous et recevez nos Super Bagels directement à votre porte.
            Fraîchement cuits au feu de bois, livrés chez vous chaque mois.
          </p>

          <div className="super-hero-cta-group">
            <a href="#super-plans" className="super-btn-primary">
              Abonnez-vous maintenant
            </a>
          </div>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <div className="super-stats-bar">
        <div className="super-stat-item">
          <div className="super-stat-icon-wrap">💪</div>
          <div>
            <span className="super-stat-value">20g</span>
            <span className="super-stat-label">de protéines</span>
          </div>
        </div>
        <div className="super-stat-item">
          <div className="super-stat-icon-wrap">🐟</div>
          <div>
            <span className="super-stat-value">Oméga-3</span>
            <span className="super-stat-label">Source d'oméga-3</span>
          </div>
        </div>
        <div className="super-stat-item">
          <div className="super-stat-icon-wrap">🌾</div>
          <div>
            <span className="super-stat-value">Haute teneur</span>
            <span className="super-stat-label">en fibres</span>
          </div>
        </div>
        <div className="super-stat-item">
          <div className="super-stat-icon-wrap">❤️</div>
          <div>
            <span className="super-stat-value">Sans</span>
            <span className="super-stat-label">cholestérol</span>
          </div>
        </div>
        <div className="super-stat-item">
          <div className="super-stat-icon-wrap">🔥</div>
          <div>
            <span className="super-stat-value">Feu de bois</span>
            <span className="super-stat-label">Cuits au feu de bois</span>
          </div>
        </div>
        <div className="super-stat-item">
          <div className="super-stat-icon-wrap">⚡</div>
          <div>
            <span className="super-stat-value">Potassium</span>
            <span className="super-stat-label">Source de potassium</span>
          </div>
        </div>
        <div className="super-stat-item">
          <div className="super-stat-icon-wrap">✨</div>
          <div>
            <span className="super-stat-value">Magnésium</span>
            <span className="super-stat-label">Excellente source</span>
          </div>
        </div>
      </div>

      {/* ── HOW IT WORKS ── */}
      <section
        className="super-section"
        style={{ background: "var(--kraft-light)" }}
      >
        <p className="super-section-eyebrow">Comment ça fonctionne</p>
        <h2 className="super-section-title">Simple comme un bagel</h2>
        <div className="super-steps">
          <div className="super-step">
            <div className="super-step-num">1</div>
            <h3>Choisissez votre plan</h3>
            <p>
              Sélectionnez la fréquence et la quantité qui vous conviennent.
              Modifiable ou annulable en tout temps, sans engagement.
            </p>
          </div>
          <div className="super-step">
            <div className="super-step-num">2</div>
            <h3>On cuit pour vous</h3>
            <p>
              Chaque fournée est préparée à Sutton, au Québec. Nos Super Bagels
              sont cuits au feu de bois pour une saveur incomparable.
            </p>
          </div>
          <div className="super-step">
            <div className="super-step-num">3</div>
            <h3>Fraîcheur garantie</h3>
            <p>
              Vos bagels sont expédiés frais, dans un emballage à l'azote, pour
              assurer qu'ils vous arrivent frais et délicieux, comme si vous
              étiez à la fabrique.
            </p>
          </div>
        </div>
      </section>

      {/* ── SUBSCRIPTION FORM ── */}
      <section className="super-plans-section" id="super-plans">
        <p className="super-section-eyebrow">Abonnement</p>
        <h2 className="super-section-title">
          Une réserve de bagels
          <br />
          haute performance frais.
          <br />
          Livrés à votre porte.
        </h2>

        <div className="super-sub-form-outer">
          <div className="super-form-intro">
            <p>
              Les bagels sont emballés de façon à pouvoir être congelés et
              utilisés au fur et à mesure du mois.
            </p>
          </div>

          <form onSubmit={submitForm} noValidate>
            {error && <div className="super-form-error">{error}</div>}

            {/* Product card (single monthly subscription) */}
            <div className="super-product-card">
              <div className="super-product-card-name">
                {SUPER_PRODUCT.name}
              </div>
              <div className="super-product-card-price">
                {SUPER_PRODUCT.price_label}
              </div>
              <div className="super-product-card-shipping">+ 7.95$ livraison</div>
              <div className="super-product-card-shipping">
                Moins de 3$ par bagel, pour 40% de vos besoins quotidiens de
                protéines.
              </div>
              <div className="super-product-card-desc">
                {SUPER_PRODUCT.description}
              </div>
            </div>

            {/* Name */}
            <div className="super-form-row">
              <label className="super-field-label">Nom</label>
              <input
                type="text"
                name="name"
                className="super-form-input"
                placeholder="Nom"
                value={name}
                onChange={handleChange}
              />
            </div>

            {/* City */}
            <div className="super-form-row">
              <label className="super-field-label">Ville</label>
              <input
                type="text"
                name="city"
                className="super-form-input"
                placeholder="Ville"
                value={city}
                onChange={handleChange}
              />
            </div>

            {/* Address + Apartment */}
            <div className="super-form-row-2">
              <div>
                <label className="super-field-label">Adresse</label>
                <input
                  type="text"
                  name="address"
                  className="super-form-input"
                  placeholder="Adresse"
                  value={address}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="super-field-label">Appartement</label>
                <input
                  type="text"
                  name="apartment"
                  className="super-form-input"
                  placeholder="Apt. (optionnel)"
                  value={apartment}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Postal + Phone */}
            <div className="super-form-row-2">
              <div>
                <label className="super-field-label">Code postal</label>
                <input
                  type="text"
                  name="postal_code"
                  className={`super-form-input ${!isValidPostalCode ? "super-input-error" : ""}`}
                  placeholder="Code Postal"
                  value={postal_code}
                  onChange={handleChange}
                />
                {!isValidPostalCode && (
                  <span className="super-field-helper">
                    SVP entrez un code postal valide
                  </span>
                )}
              </div>
              <div>
                <label className="super-field-label">Téléphone</label>
                <div className="super-phone-row">
                  <input
                    type="text"
                    className="super-form-input super-phone-code"
                    value={AREA_CODE}
                    readOnly
                  />
                  <input
                    type="tel"
                    name="phone_number"
                    className={`super-form-input ${phoneNumberError ? "super-input-error" : ""}`}
                    placeholder="Téléphone #"
                    value={phone_number}
                    onChange={handleChange}
                    style={{ flex: 1 }}
                  />
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="super-form-row">
              <label className="super-field-label">E-mail</label>
              <input
                type="email"
                name="email"
                className="super-form-input"
                placeholder="E-mail"
                value={email}
                onChange={handleChange}
              />
            </div>

            {/* Checkboxes */}
            <div className="super-check-group">
              <label className="super-check-item">
                <input
                  type="checkbox"
                  name="accept_sms_notification"
                  checked={accept_sms_notification}
                  onChange={handleChange}
                />
                <span>
                  J'accepte de recevoir des alertes de livraisons par texto
                </span>
              </label>
              <label className="super-check-item">
                <input
                  type="checkbox"
                  name="accept_email_notification"
                  checked={accept_email_notification}
                  onChange={handleChange}
                />
                <span>
                  J'accepte de recevoir des infolettres occasionnels par
                  courriel
                </span>
              </label>
            </div>

            <button
              type="submit"
              className="super-btn-primary"
              style={{ width: "100%", fontSize: "1.05rem", padding: "18px" }}
              disabled={isDisabled}
            >
              Confirmer mon abonnement
            </button>

            <p className="super-skip-note">
              © {new Date().getFullYear()} Bagels Round Top Inc. ·{" "}
              <a href="/privacy-policy">Politique de vie privée</a>
            </p>
          </form>

          <span
            className="super-unsubscribe-link"
            onClick={() => setShowUnsubscribeModal(true)}
          >
            Souhaitez-vous vous <strong>désabonner</strong>?
          </span>
        </div>
      </section>

      {/* ── ORIGIN ── */}
      <section className="super-origin-section">
        <div className="super-origin-inner">
          <div className="super-origin-illustration">
            <svg
              viewBox="0 0 400 300"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ borderRadius: 16 }}
            >
              <rect width="400" height="300" fill="#C9A96E" rx="14" />
              <circle cx="340" cy="55" r="30" fill="#C84B2F" opacity="0.6" />
              <circle cx="340" cy="55" r="20" fill="#EDD9A0" opacity="0.5" />
              <path
                d="M0 300L80 180L170 220L270 140L360 200L400 165V300H0Z"
                fill="#4A7A30"
                opacity="0.35"
              />
              <path
                d="M0 300L100 210L190 175L290 200L400 155V300H0Z"
                fill="#2A5518"
              />
              <path d="M172 175L190 140L208 175Z" fill="white" opacity="0.85" />
              <path d="M383 155L400 118L400 155Z" fill="white" opacity="0.65" />
              <path d="M0 300L140 248L270 265L400 225V300H0Z" fill="#1E3D10" />
              <g fill="#1E3D10">
                <polygon points="20,258 36,232 52,258" />
                <polygon points="14,270 36,226 58,270" />
                <polygon points="48,262 66,236 84,262" />
                <polygon points="44,274 66,230 88,274" />
              </g>
              <g fill="#1E3D10">
                <polygon points="318,242 334,216 350,242" />
                <polygon points="312,254 334,210 356,254" />
                <polygon points="348,246 366,220 384,246" />
                <polygon points="344,258 366,214 388,258" />
              </g>
              <g transform="translate(178, 225)" fill="#1E3D10">
                <circle cx="10" cy="0" r="6" />
                <rect x="6" y="8" width="8" height="18" rx="2" />
                <rect x="0" y="13" width="8" height="2.5" rx="1" />
                <rect x="10" y="13" width="8" height="2.5" rx="1" />
                <rect x="5" y="26" width="4.5" height="16" rx="1" />
                <rect x="10" y="26" width="4.5" height="16" rx="1" />
                <rect x="20" y="6" width="2" height="38" rx="1" />
              </g>
              <g
                transform="translate(230, 232)"
                fill="none"
                stroke="#1E3D10"
                strokeWidth="2.5"
                opacity="0.75"
              >
                <circle cx="10" cy="12" r="10" />
                <circle cx="40" cy="12" r="10" />
                <line x1="25" y1="-10" x2="25" y2="5" />
                <line x1="25" y1="5" x2="10" y2="12" />
                <line x1="25" y1="5" x2="40" y2="12" />
                <line x1="25" y1="-10" x2="17" y2="-2" />
                <line x1="25" y1="-10" x2="33" y2="-2" />
                <circle cx="25" cy="-15" r="6" fill="#1E3D10" stroke="none" />
              </g>
              <rect
                x="10"
                y="12"
                width="108"
                height="38"
                rx="7"
                fill="#1E3D10"
              />
              <text
                x="64"
                y="27"
                textAnchor="middle"
                fill="#EDD9A0"
                fontFamily="Cabin, sans-serif"
                fontSize="9"
                fontWeight="700"
              >
                INGRÉDIENTS DU
              </text>
              <text
                x="64"
                y="42"
                textAnchor="middle"
                fill="white"
                fontFamily="Cabin, sans-serif"
                fontSize="12"
                fontWeight="700"
              >
                QUÉBEC
              </text>
              <rect
                x="290"
                y="10"
                width="100"
                height="38"
                rx="7"
                fill="#C84B2F"
              />
              <text
                x="340"
                y="25"
                textAnchor="middle"
                fill="white"
                fontFamily="Cabin, sans-serif"
                fontSize="8"
                fontWeight="700"
              >
                CUITS AU
              </text>
              <text
                x="340"
                y="38"
                textAnchor="middle"
                fill="#EDD9A0"
                fontFamily="Cabin, sans-serif"
                fontSize="9.5"
                fontWeight="700"
              >
                FEU DE BOIS
              </text>
            </svg>
          </div>

          <div className="super-origin-text">
            <p className="super-section-eyebrow">Notre origine</p>
            <h2 className="super-origin-title">
              Fabriqués à<br />
              Sutton, <span>Québec</span>
            </h2>
            <p>
              Au cœur des Cantons-de-l'Est, nous cuisons chaque bagel au feu de
              bois avec des ingrédients soigneusement sélectionnés. Un
              savoir-faire artisanal, une qualité nutritive exceptionnelle.
            </p>
            <p>
              Nos Super Bagels sont{" "}
              <strong>faits principalement d'ingrédients du Québec</strong> — un
              choix local pour les amateurs de plein air et de vie active.
              Source de potassium, excellente source de magnésium, sans
              cholestérol.
            </p>
            <div className="super-origin-badges">
              <div className="super-badge-pill">🏔️ Sutton, Québec</div>
              <div className="super-badge-pill">🔥 Feu de bois</div>
              <div className="super-badge-pill">🌿 Ingrédients du Québec</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="super-footer">
        <div className="super-footer-inner">
          <div className="super-footer-brand">
            <strong>Bagels Round Top</strong>
            <span>Sutton, Québec · Ingrédients du Québec</span>
          </div>
          <div className="super-footer-links">
            <div className="super-footer-col">
              <h4>Navigation</h4>
              <a href="#super-plans">Abonnement</a>
              <a href="/contact-us">Joignez-nous</a>
              <a href="/about-us">À propos de nous</a>
              <a href="/arbres">Arbres</a>
            </div>
            <div className="super-footer-col">
              <h4>Mon compte</h4>
              <a href="/residents">Offres pour les résidents</a>
              <button
                type="button"
                className="super-footer-link-btn"
                onClick={() => setShowUnsubscribeModal(true)}
              >
                Gérer mon abonnement
              </button>
            </div>
            <div className="super-footer-col">
              <h4>Nous rejoindre</h4>
              <a href="https://instagram.com" target="_blank" rel="noreferrer">
                Instagram
              </a>
              <a href="https://facebook.com" target="_blank" rel="noreferrer">
                Facebook
              </a>
              <a href="/contact-us">Nous écrire</a>
            </div>
          </div>
        </div>
        <div className="super-footer-bottom">
          © {new Date().getFullYear()} Bagels Round Top. Tous droits réservés.
          &nbsp;·&nbsp; Sutton, Québec
        </div>
      </footer>
    </div>
  );
};

export default Super;
